import * as React from "react";

import styled from "styled-components";

import { SWReactCountryIcons } from "@similarweb/icons";
import { colorsPalettes, rgba } from "@similarweb/styles";
import {
    Button,
    Dropdown,
    EllipsisDropdownItem,
    IconButton,
    PlainTooltip,
    SimpleLegend,
} from "@similarweb/ui-components";

import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import I18n from "pro-features/components/WithTranslation/src/I18n";
import { minVisitsAbbrFilter, percentageSignFilter } from "app/filters/ngFilters";
import { prettifyCategoryFilter } from "app/filters/prettifyCategoryFilter";
import WrappedWithQuotaTooltip from "app/pages/sales-intelligence/common-components/WrappedWithQuotaTooltip/WrappedWithQuotaTooltip";
import useStaticListSizeQuota from "app/pages/sales-intelligence/hooks/useStaticListSizeQuota";

import WithTranslation from "../../../../components/WithTranslation/src/WithTranslation";
import { FlexColumn, FlexRow } from "../../../../styled components/StyledFlex/src/StyledFlex";
import type { workspaceContextType } from "../WorkspaceContext";
import { WorkspaceContext } from "../WorkspaceContext";

import {
    BottomTitle,
    ButtonsWrapper,
    LinkIcon,
    RecommendationsSidebarDotsWrapper,
    RecommendationsSidebarSubtitle,
    RecommendationsSidebarTileBottomFragment,
    RecommendationsSidebarTileTopFragment,
    RecommendationsSidebarTileWebsite,
    RecommendationsSidebarTileWebsiteContainer,
    RecommendedWebsiteWrapper,
    Subtitle,
} from "./StyledComponents";

export interface IRecommendationTile {
    Domain: string;
    FavIcon: string;
    Removed?: boolean;
    Category: string;
    TopCountry: number;
    TopCountryName: string;
    Visits: number;
    Change: number;
    Description: string;
}

interface IRecommendationTileProps extends IRecommendationTile {
    onAddRecommendations: (domainsArr: FixmeType, holdRightBarOpen?: boolean) => void;
    onDismissRecommendation: (domain: string) => void;
    onLinkRecommendation: (domain: string) => void;
    translate: (key: string, obj?: FixmeType) => string;
    track: (category?: string, action?: string, name?: string, value?: FixmeType) => void;
}

const RecommendationsTopTitle = ({ recommendations, country, translate }: FixmeType) => {
    return (
        <RecommendationsSidebarSubtitle>
            {translate("workspace.recommendation_sidebar.results.title", {
                results: recommendations.filter((r: FixmeType) => !r.Removed).length.toString(),
                country,
            })}
        </RecommendationsSidebarSubtitle>
    );
};

const RecommendationsSidebarContent: React.FC<any> = React.memo<any>(
    ({ track, translate, onAddRecommendations, recommendations, websiteActions }: FixmeType) => {
        const onClickAddAll = () => {
            const recommendationsToAdd = recommendations
                .filter((r: FixmeType) => !r.Removed)
                .map(({ Domain }: FixmeType) => ({ Domain }));
            onAddRecommendations(recommendationsToAdd, true);
            track("Recommendation model", "click", `add all websites/${recommendationsToAdd.length}`);
        };

        return (
            <WorkspaceContext.Consumer>
                {({ activeOpportunitiesList, getCountryById, onFetchRecommendations }: workspaceContextType) => (
                    <>
                        <RecommendationsTopTitle
                            recommendations={recommendations}
                            translate={translate}
                            country={getCountryById(activeOpportunitiesList.country).text}
                        />

                        <ButtonsWrapper>
                            <Button
                                type="flat"
                                onClick={onClickAddAll}
                                dataAutomation="recommendations-sidebar-add-all"
                            >
                                {translate("workspace.recommendation_sidebar.button")}
                            </Button>
                            <Button
                                type="flat"
                                onClick={onFetchRecommendations}
                                dataAutomation="recommendations-sidebar-reload"
                            >
                                {translate("workspace.recommendation_sidebar.refresh")}
                            </Button>
                        </ButtonsWrapper>
                        {recommendations.map((crrWebsite: FixmeType) => (
                            <RecommendationTile
                                key={crrWebsite.Domain}
                                onAddRecommendations={onAddRecommendations}
                                {...crrWebsite}
                                {...websiteActions}
                                translate={translate}
                                track={track}
                            />
                        ))}
                    </>
                )}
            </WorkspaceContext.Consumer>
        );
    },
);

RecommendationsSidebarContent.displayName = "RecommendationsSidebarContent";

const LegendAndLinkContainer = styled(FlexRow)`
    align-items: center;
    height: 24px;
`;

const GranularCategory = styled(Subtitle)`
    position: relative;
    top: 2px;
`;

const Description = styled(Subtitle)`
    opacity: 0.8;
    text-overflow: ellipsis;
    display: -webkit-box;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-height: 18px;
    margin: -12px 0 0 31px;
    max-width: 302px;
`;

const RecommendedWebsite = ({ domain, icon, category, description }: FixmeType) => {
    const granularCategory =
        typeof category === "string" && prettifyCategoryFilter()(category, null, null)?.match(/[^/]+$/)?.[0]; // we extract the string after the last "/"
    return (
        <RecommendedWebsiteWrapper>
            <LegendAndLinkContainer>
                <SimpleLegend items={[{ icon, name: domain }]} />
                <LinkIcon
                    href={`http://${domain}`}
                    target={"_blank"}
                    data-automation="recommendation-tile-link-out"
                >
                    <IconButton
                        iconName="link-out"
                        type="flat"
                        iconSize="xs"
                    />
                </LinkIcon>
            </LegendAndLinkContainer>
            {granularCategory ? <GranularCategory>{granularCategory}</GranularCategory> : null}
        </RecommendedWebsiteWrapper>
    );
};
export const CountryIconWithText = styled.span`
    margin-left: 4px;
    max-width: 120px;
    display: inline-block;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
`;

const MetricColumn = styled(FlexColumn)`
    box-sizing: border-box;
    justify-content: space-between;
    height: 40px;
`;

const CountryColumn = styled(MetricColumn)`
    flex-basis: 152px;
    padding-left: 12px;
`;

const ChangeColumn = styled(MetricColumn)`
    flex-basis: 89px;
    padding-right: 7px;
    border-right: 1px solid ${rgba(colorsPalettes.carbon[500], 0.08)};
`;

const GlobalVisitsColumn = styled(MetricColumn)`
    flex-basis: 97px;
    padding-right: 12px;
`;

const BottomSubtitle = styled(Subtitle)`
    opacity: 0.8;
`;

const CollectedMetrics = ({ country, countryName, change, Visits }: FixmeType) => (
    <WithTranslation>
        {(translate) => (
            <>
                <GlobalVisitsColumn>
                    <BottomSubtitle>{translate("global.visits")}</BottomSubtitle>
                    <BottomTitle>
                        <SWReactCountryIcons
                            countryCode={999}
                            size={"xs"}
                        />
                        <CountryIconWithText>{minVisitsAbbrFilter()(Visits)}</CountryIconWithText>
                    </BottomTitle>
                </GlobalVisitsColumn>
                <ChangeColumn>
                    <BottomSubtitle>{translate("global.change.mom")}</BottomSubtitle>
                    <BottomTitle
                        // @ts-ignore FIXME: strict migration - Type 'string | null' is not assignable to 'string | undefined' — BottomTitle color prop, preserving legacy null on zero-change
                        color={change > 0 ? colorsPalettes.green.s100 : change < 0 ? colorsPalettes.red.s100 : null}
                    >
                        {(change > 0 ? "+" : "") + percentageSignFilter()(change, 2)}
                    </BottomTitle>
                </ChangeColumn>
                <CountryColumn>
                    <BottomSubtitle>{translate("workspace.recommendations.top_country")}</BottomSubtitle>
                    <BottomTitle>
                        <SWReactCountryIcons
                            countryCode={country}
                            size={"xs"}
                        />
                        <CountryIconWithText>{countryName}</CountryIconWithText>
                    </BottomTitle>
                </CountryColumn>
            </>
        )}
    </WithTranslation>
);

const AlignRight = styled.div`
    display: flex;
    align-self: flex-end;
    flex-grow: 1;
`;

export const RecommendationTile = React.memo(
    ({
        Domain,
        FavIcon,
        Category,
        TopCountry,
        TopCountryName,
        Visits,
        Change,
        Removed,
        onAddRecommendations,
        onDismissRecommendation,
        onLinkRecommendation,
        translate,
        track,
        Description: description,
    }: IRecommendationTileProps) => {
        const { isListSizeQuotaReached } = useStaticListSizeQuota();
        const canDeleteOpportunities = salesSettingsHelper.canDeleteOpportunities();

        const dropdownOptions = [
            {
                label: translate("workspace.recommendation_sidebar.dismiss"),
                action: (domain: string) => {
                    onDismissRecommendation(domain);
                    track("Recommendation model", "click", `Settings/Dismiss recommendation/${domain}`);
                },
            },
            {
                label: translate("workspace.recommendation_sidebar.website_analysis"),
                action: (domain: string) => {
                    onLinkRecommendation(domain);
                    track("Recommendation model", "click", `Settings/see website analysis/${domain}`);
                },
            },
        ];

        const onDropdownComponentClick = ({ id }: FixmeType) => dropdownOptions[id].action(Domain);

        const onDropdownToggle = (isOpen: boolean) => {
            if (isOpen) {
                track("Recommendation model", "open", "Settings");
            }
        };

        const onClickAddDomain = () => {
            onAddRecommendations([{ Domain }], true);
            track("Recommendation model", "click", `Add recommendation/${Domain}`);
        };

        return (
            <RecommendationsSidebarTileWebsiteContainer
                isRemoved={!!Removed}
                data-automation="recommendations-sidebar-tile-container"
            >
                <RecommendationsSidebarTileWebsite
                    isRemoved={!!Removed}
                    data-automation="recommendations-sidebar-tile"
                >
                    <RecommendationsSidebarTileTopFragment>
                        <RecommendedWebsite
                            icon={FavIcon}
                            domain={Domain}
                            category={Category}
                            description={Description}
                        />
                        <AlignRight>
                            <RecommendationsSidebarDotsWrapper>
                                <Dropdown
                                    width={224}
                                    onClick={onDropdownComponentClick}
                                    onToggle={onDropdownToggle}
                                >
                                    {[
                                        <IconButton
                                            key="ddbtn"
                                            type="flat"
                                            iconName="dots-more"
                                            dataAutomation="recommendations-sidebar-settings"
                                        />,
                                        ...dropdownOptions.map((option, idx) => (
                                            <EllipsisDropdownItem
                                                key={`dditem_${idx}`}
                                                // @ts-expect-error Type number is not assignable to type string
                                                id={idx}
                                            >
                                                {option.label}
                                            </EllipsisDropdownItem>
                                        )),
                                    ]}
                                </Dropdown>
                            </RecommendationsSidebarDotsWrapper>
                            <WrappedWithQuotaTooltip
                                placement="top"
                                isListSizeQuotaReached={isListSizeQuotaReached}
                                isTrialTooltip={!canDeleteOpportunities}
                            >
                                <Button
                                    type="outlined"
                                    dataAutomation="recommendations-sidebar-add-domain"
                                    onClick={onClickAddDomain}
                                    isDisabled={isListSizeQuotaReached}
                                >
                                    {translate("workspace.recommendations.add")}
                                </Button>
                            </WrappedWithQuotaTooltip>
                        </AlignRight>
                    </RecommendationsSidebarTileTopFragment>
                    <Description>
                        {description ? (
                            <PlainTooltip tooltipContent={description}>
                                <span>{description}</span>
                            </PlainTooltip>
                        ) : (
                            <I18n>workspace.recommendation_sidebar.no.description</I18n>
                        )}
                    </Description>
                    <RecommendationsSidebarTileBottomFragment>
                        <CollectedMetrics
                            change={Change}
                            country={TopCountry}
                            countryName={TopCountryName}
                            Visits={Visits}
                        />
                    </RecommendationsSidebarTileBottomFragment>
                </RecommendationsSidebarTileWebsite>
            </RecommendationsSidebarTileWebsiteContainer>
        );
    },
);
